import { useEffect, useRef } from 'react'
import { taskMenuTargetForKey } from '../../TaskMenu/taskMenuShortcuts'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

interface UseCanvasPlacementShortcutOptions {
  /** Im sichtbaren Tab (TabStack-Falle). */
  enabled: boolean
  /**
   * Die Pinnwand ist zu. Dann gelten nur `N` und `M` — sie holen sie herein
   * wie Notiz und Memo der **Listen-Werkzeug-Pille** (#229). `T` und `⇧S`
   * haben ihre Knöpfe nur an der offenen Pinnwand und schweigen.
   */
  closed?: boolean
  onPlaceSticky: () => void
  /** `M` — stellt ein **Memo** scharf. */
  onPlaceMemo: () => void
  /** `⇧S` — stellt einen **Bereich** scharf. Optional, bis jeder Aufrufer ihn kennt. */
  onPlaceArea?: () => void
  /** `T` — stellt einen **Text** scharf. Optional wie der Bereich. */
  onPlaceText?: () => void
}

/**
 * `N` stellt einen neuen **Notizen** scharf — als hätte man in der
 * Werkzeug-Pille auf „Notiz" gedrückt. Der nächste Klick auf die Fläche
 * setzt ihn ab.
 *
 * Die Taste steht für „Notiz". Die Fläche selbst liegt auf `C`
 * (`useCanvasPanelShortcut`) — dieselbe Aufteilung wie in FigJam: eine Taste
 * für die Fläche, eine für das Element darauf.
 *
 * Blanker Buchstabe, also mit denselben Vorsichten wie die Projekt-Kürzel
 * (A/B/F in `useProjectKeyboardShortcuts`) und die Werkzeuge (V/H): kein
 * Modifikator, nichts während getippt wird, nichts bei offenem Menü.
 * Insbesondere ⌘N (neue Aufgabe) und ⌘⌥N (neues Projekt)
 * bleiben unangetastet.
 *
 * @example
 * useCanvasPlacementShortcut({ enabled: isActiveTab, onPlaceSticky: …, onPlaceMemo: … })
 */
export function useCanvasPlacementShortcut({ enabled, closed = false, onPlaceSticky, onPlaceMemo, onPlaceArea, onPlaceText }: UseCanvasPlacementShortcutOptions) {
  const onPlaceStickyRef = useRef(onPlaceSticky)
  onPlaceStickyRef.current = onPlaceSticky
  const onPlaceMemoRef = useRef(onPlaceMemo)
  onPlaceMemoRef.current = onPlaceMemo
  const onPlaceAreaRef = useRef(onPlaceArea)
  onPlaceAreaRef.current = onPlaceArea
  const onPlaceTextRef = useRef(onPlaceText)
  onPlaceTextRef.current = onPlaceText

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘N legt eine neue Aufgabe an, ⌘⌥N ein Projekt.
      // Anders als beim Canvas-Kürzel ist auch ⇧ ausgeschlossen: Ein zweites
      // „N" für eine andere Größe gibt es hier nicht.
      if (e.metaKey || e.ctrlKey || e.altKey) return
      /*
        ⇧ ist die EINE Ausnahme, und nur für `S`: Der **Bereich** liegt auf
        ⇧S (Grillen 2026-09-04) — blankes `S` bleibt frei, und ⇧N/⇧M tun
        weiter nichts. Ein zweites „N" für eine andere Größe gibt es hier
        nicht (siehe oben).
      */
      const area = e.shiftKey && e.code === 'KeyS'
      if (e.shiftKey && !area) return
      // Ein offenes Menü behält seine Tasten für sich — die Konvention dieser
      // App, siehe `useProjectKeyboardShortcuts` und `useProjectNavigation`.
      if (document.querySelector('[role="menu"]')) return
      // Im Text einer Notiz und in jedem Aufgabenfeld ist „n" ein
      // Buchstabe.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Bei
      // geteiltem Canvas liegen sie als Slide-over darüber, und der Fokus steht
      // dort meist auf einem KNOPF — am INPUT/TEXTAREA-Wächter oben vorbei.
      if (isInsideTaskDetails(e.target)) return
      /*
        **Die Aufgabenliste behält ihre Buchstaben.**

        `T` gehört dort den **Schlagwörtern** (`taskMenuShortcuts.ts`). Beide
        Kürzel hören am `window`, keines weiß vom anderen — ohne diese Wache
        stellte ein `T` in der Liste einen Text scharf und öffnete zugleich
        die Schlagwörter. Bis #229 betraf das auch `M`; die Aufmerksamkeit
        liegt seitdem auf `⇧M`, blankes `M` ist überall das Memo.

        Gefragt wird nach dem TASTATURFOKUS, nicht danach, ob eine Aufgabe
        ausgewählt ist: Die Regel soll auch dann gelten, wenn gerade keine
        Zeile markiert ist — wer in der Liste steht, meint die Liste.

        Welche Tasten umstritten sind, steht nicht hier, sondern in der
        Tabelle des Aufgaben-Menüs — zwei getippte Listen liefen auseinander.
      */
      const inTaskList = document.activeElement?.closest('.project-page__task-list-wrap')
      if (inTaskList && taskMenuTargetForKey(e)) return
      // `code` statt `key`, damit das Kürzel auf jedem Layout dort liegt, wo
      // es eingezeichnet ist.
      /*
        `N` Notiz, `M` Memo — **jeder Buchstabe steht für seinen Namen.**

        Bis 2026-09-06 hießen die beiden „Gedanke" und „Notiz", und die Tasten
        hingen an ihrer NACHBARSCHAFT: N links wie der Gedanke links in der
        Pille. Das war in sich stimmig, aber unsichtbar — und es verlor gegen
        die viel stärkere Erwartung „N wie Notiz", die auf das eine zeigte und
        das andere hieß. Die Namen liegen seitdem auf einer Achse (beides
        Zettel, der eine hingeworfen, der andere gesetzt), und die Tasten
        mussten sich dafür nicht bewegen (ADR 0014).
      */
      // `T` für den **Text** — umstritten mit den Schlagwörtern der Liste
      // (`taskMenuShortcuts`), die Wache oben lässt sie ihr.
      if (!area && e.code !== 'KeyN' && e.code !== 'KeyM' && e.code !== 'KeyT') return
      if (closed && (area || e.code === 'KeyT')) return
      if (area && !onPlaceAreaRef.current) return
      if (e.code === 'KeyT' && !onPlaceTextRef.current) return

      e.preventDefault()
      if (area) onPlaceAreaRef.current?.()
      else if (e.code === 'KeyN') onPlaceStickyRef.current()
      else if (e.code === 'KeyT') onPlaceTextRef.current?.()
      else onPlaceMemoRef.current()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, closed])
}
